import { type CSSProperties, type JSX, useCallback, useEffect, useMemo, useState } from 'react';

import type { Engine } from '@runtime/engine.js';
import type { FeatureFlag } from '@schema/features.js';
import type { Profile } from '@schema/profile.js';
import type { Palette } from '@schema/index.js';
import type { FolderTile, Tile } from '@schema/tiles.js';

import {
  loadDismissedIds,
  persistDismissedIds,
  useBroadcastPoller,
} from '../broadcasts.js';
import { resolveBroadcastsUrl } from '../tenant.js';
import { PLATFORM_VERSION } from '../version.js';
import { BroadcastPanel } from './launcher/BroadcastPanel.js';
import { LauncherFooter } from './launcher/LauncherFooter.js';
import { LauncherHeader } from './launcher/LauncherHeader.js';
import { useCurrentUser } from './use-current-user.js';
import { useDeviceStatus } from './use-device-status.js';
import { LauncherHero } from './launcher/LauncherHero.js';
import { CalculatorModal } from './launcher/modals/CalculatorModal.js';
import { DeviceInfoModal } from './launcher/modals/DeviceInfoModal.js';
import { DnsPingModal } from './launcher/modals/DnsPingModal.js';
import { SpeedTestModal } from './launcher/modals/SpeedTestModal.js';
import { FolderModal } from './launcher/modals/FolderModal.js';
import { ToolsModal } from './launcher/modals/ToolsModal.js';
import { TileGrid } from './TileGrid.js';

type ModalKind =
  | 'device-info'
  | 'tools'
  | 'calculator'
  | 'dns-ping'
  | 'speed-test'
  | 'broadcasts';

export interface ProfileViewProps {
  profile: Profile;
  features: ReadonlySet<FeatureFlag>;
  onTileActivate: (tile: Tile) => Promise<void> | void;
  /** Device ID / serial shown in the header dropdown. */
  deviceLabel?: string;
  /**
   * Optional engine reference — needed for the Device Info modal's
   * tier/feature/OEM fields. When omitted, the modals are still openable
   * but Device Info shows placeholders only.
   */
  engine?: Engine;
  /**
   * Explicit tenant id for broadcast polling. The Builder preview lives
   * at /preview (no /t/:tenantId path) and receives the tenantId via
   * postMessage, so it passes it through here. Production launcher
   * paths (/t/acme) leave this unset and the resolver falls back to
   * URL-based detection.
   */
  tenantIdOverride?: string;
  /**
   * Reload the launcher. Threaded to the header's "Refresh now" menu
   * item. The real launcher (App.tsx) passes window.location.reload;
   * the Builder preview leaves it unset so the menu item is hidden.
   */
  onRefresh?: () => void;
}

const CUSTOM_FONT_FAMILY = 'mobilauncher-custom';

/**
 * Renders an active profile as a full launcher screen:
 *   header → hero → featured launch → tile grid → footer.
 *
 * The status bar that previously sat above the grid has been folded into
 * `LauncherHeader` (kiosks already get the OS clock in a status overlay;
 * the bar's job is now branding + service entry points).
 *
 * Theme application:
 *   - Core honors `background` / `primary` / `accent` only.
 *   - Pro (`theming` flag) unlocks `surface` / `text` / `textMuted`,
 *     `logoRef`, `wallpaperRef`, `fontRef`, and `darkMode`.
 *
 * The custom font is registered via a dynamically-injected `@font-face`
 * stylesheet so the asset URL only loads when entitlement permits.
 */
export function ProfileView({
  profile,
  features,
  onTileActivate,
  deviceLabel,
  engine,
  tenantIdOverride,
  onRefresh,
}: ProfileViewProps): JSX.Element {
  const proTheming = features.has('theming');
  /**
   * SSO status-bar pill. Gated on config + the ssoDisplay entitlement;
   * only then do we ask the agent host for the signed-in user. Resolves
   * to undefined off-device / without the capability, and LauncherHeader
   * falls back to a neutral label.
   */
  const showSso = profile.statusBar.showCurrentUser === true && features.has('ssoDisplay');
  const currentUser = useCurrentUser(engine, showSso);
  /**
   * Battery + signal for the status bar. Only polled when the profile asks
   * for either indicator; the agent host returns real values on MDM devices
   * and undefined elsewhere (so the header renders nothing rather than a
   * misleading stub).
   */
  const deviceStatus = useDeviceStatus(
    engine,
    profile.statusBar.showBattery === true || profile.statusBar.showSignal === true,
  );
  /**
   * Modal navigation stack. Opening a modal from another modal pushes
   * onto the stack so the new dialog gets a "← Back" affordance. Close
   * (or Escape, or backdrop click) always clears the whole stack —
   * Back is for in-flow navigation only.
   */
  const [modalStack, setModalStack] = useState<ModalKind[]>([]);
  const openModal: ModalKind | null = modalStack[modalStack.length - 1] ?? null;
  const canGoBack = modalStack.length > 1;
  /**
   * Currently-open folder. Folder taps are intercepted in
   * `handleTileTap` below so they don't fall through to the engine —
   * the folder modal is a navigational view, not a launchable action.
   */
  const [openFolder, setOpenFolder] = useState<FolderTile | null>(null);
  /**
   * In-launcher tile search. Only surfaced when the grid is dense enough
   * to be worth searching (see SEARCH_MIN_TILES). While a query is
   * active the hero collapses and the grid shows just the matching
   * launchable tiles (spacers / dividers are layout, so they drop out).
   */
  const [tileQuery, setTileQuery] = useState('');

  function openTop(kind: ModalKind): void {
    setModalStack([kind]);
  }
  function pushModal(kind: ModalKind): void {
    setModalStack((s) => [...s, kind]);
  }
  function popModal(): void {
    setModalStack((s) => s.slice(0, -1));
  }
  function closeAllModals(): void {
    setModalStack([]);
    setOpenFolder(null);
  }
  const onBack = canGoBack ? popModal : undefined;

  /**
   * Tap dispatcher for tiles rendered in the grid + hero. Folder
   * taps open the FolderModal instead of going through the engine.
   * Everything else hands off to the parent's `onTileActivate`.
   */
  function handleTileTap(tile: Tile): Promise<void> | void {
    if (tile.type === 'folder') {
      setOpenFolder(tile);
      return;
    }
    return onTileActivate(tile);
  }

  // Broadcasts (Pro+): poll every 60s when entitled AND a tenant URL is
  // resolvable. Core / Free tier devices skip the poller entirely so the
  // licensing-server never sees their traffic, and the bell stays hidden.
  const broadcastsEnabled = features.has('broadcasts');
  const broadcastsUrl = useMemo(
    () =>
      broadcastsEnabled
        ? resolveBroadcastsUrl(undefined, tenantIdOverride)
        : null,
    [broadcastsEnabled, tenantIdOverride],
  );
  const allBroadcasts = useBroadcastPoller({ url: broadcastsUrl });
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => loadDismissedIds());
  const visibleBroadcasts = useMemo(
    () => allBroadcasts.filter((b) => !dismissedIds.has(b.id)),
    [allBroadcasts, dismissedIds],
  );
  const unreadBroadcasts = broadcastsEnabled ? visibleBroadcasts.length : undefined;

  function dismissBroadcast(id: string): void {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      persistDismissedIds(next);
      return next;
    });
  }

  // Mode-aware OS color-scheme detection. Subscribed so the launcher
  // flips when the device's OS theme changes mid-session (Android dark-
  // mode toggle). The cssVars block further down picks light vs dark
  // palette based on the resolved mode; previous attempts to do this
  // via a scoped <style> failed because selector specificity can't beat
  // inline-style specificity, so the inline LIGHT values won in dark
  // mode and paletteDark never painted.
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent): void => setSystemPrefersDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  /**
   * Resolved hero-band image source. Wallpaper is now scoped to the
   * hero only (not the whole main area) — keeps the tile grid below
   * legible against the background color and gives admins a single
   * "branded slab" to show off. Precedence:
   *   1. wallpaperDataUrl  (Portal upload, inline base64)
   *   2. wallpaperRef      (build-time asset bundle)
   *   3. heroImageRef      (legacy field — same purpose)
   */
  const heroImageSrc = proTheming
    ? profile.theme.wallpaperDataUrl
      ?? (profile.theme.wallpaperRef !== undefined
        ? resolveAsset(profile.theme.wallpaperRef)
        : undefined)
      ?? (profile.theme.heroImageRef !== undefined
        ? resolveAsset(profile.theme.heroImageRef)
        : undefined)
    : undefined;

  /**
   * Resolved logo source. Precedence mirrors heroImageSrc:
   *   1. logoDataUrl  (Portal-uploaded data URL)
   *   2. logoRef      (build-time asset bundle)
   * Both are Pro-gated; Core tenants render the default briefcase icon.
   */
  const logoSrc = proTheming
    ? profile.theme.logoDataUrl
      ?? (profile.theme.logoRef !== undefined
        ? resolveAsset(profile.theme.logoRef)
        : undefined)
    : undefined;

  useEffect(() => {
    if (!proTheming || profile.theme.fontRef === undefined) return undefined;
    const ref = profile.theme.fontRef;
    const style = document.createElement('style');
    style.dataset['mobilauncherFont'] = '1';
    style.textContent = `@font-face { font-family: '${CUSTOM_FONT_FAMILY}'; src: url(${JSON.stringify(resolveAsset(ref))}); font-display: swap; }`;
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }, [proTheming, profile.theme.fontRef]);

  // Device-local theme override. Every tier gets a "Theme" item in the
  // header menu that cycles auto → always → never; the choice persists
  // per-device in localStorage and wins over the config's darkMode. We
  // store null when the user hasn't overridden, so a customer who later
  // ships a new config still gets to change the launcher's default.
  // (Light/dark is a basic display preference, honored at every tier —
  // see the effectiveThemeMode note below; only the richer Pro theming
  // chrome stays gated behind proTheming.)
  //
  // EXCEPTION: when we're embedded in the Builder's live-preview iframe
  // (which is the *only* legitimate iframed embedding), ignore the
  // localStorage override entirely. The Builder's "Default mode"
  // dropdown is the authoritative controller in that context, and a
  // stale per-device override from a prior preview-iframe Theme-cycle
  // click would mask any darkMode change the user makes in the
  // Builder. The cycleThemeMode handler below ALSO skips persistence
  // when embedded, so clicking Theme in the preview's header menu
  // updates the live view (so the customer can still see what dark
  // mode looks like) without poisoning future previews.
  const isEmbedded =
    typeof window !== 'undefined' && window.self !== window.top;
  const [themeOverride, setThemeOverride] = useState<ThemeMode | null>(
    () => (isEmbedded ? null : readThemeOverride()),
  );
  // When the theme doesn't pin a darkMode, fall back to `'auto'` so the
  // launcher follows the device's OS color-scheme. This matches the
  // ThemeEditor's UI default (the dropdown shows "auto" when unset), so
  // a Pro+ tenant who never touches the field gets OS-aware dark mode
  // instead of a permanently-light launcher.
  const configDarkMode = profile.theme.darkMode ?? 'auto';
  const effectiveThemeMode: ThemeMode = themeOverride ?? configDarkMode;
  // Light / dark / auto mode flows at every tier — it's a basic display
  // preference, not a Pro feature. Pro still gates the richer chrome
  // palette + wallpaper / logo / custom font (see proTheming gates
  // above), but Core gets to honor `theme.darkMode`.
  const dataTheme = effectiveThemeMode;

  // ─── Theme cascade ───────────────────────────────────────────────
  //
  // Single source of truth for the inline cssVars on .profile-view.
  //
  // LIGHT mode: flat ProTheme fields (background/primary/accent/…) are
  // the back-compat base, and paletteLight overlays on top. Anything
  // un-set falls through to :root defaults in styles/index.css.
  //
  // DARK mode: paletteDark is authoritative; unset fields fall through
  // to the renderer's dark @media + data-theme blocks. Flat fields are
  // NOT written in dark mode because flat values are light-mode values
  // — writing them inline would beat the dark @media defaults via
  // inline specificity (the exact bug this fixes). For back-compat
  // configs that have flat fields but no paletteDark, we still carry
  // primary + accent (mode-agnostic brand colors), but skip the
  // surface fields so the renderer's dark defaults can paint.
  //
  // Why paletteLight + paletteDark are applied at every tier (not
  // proTheming-gated like before): the starter config ships them with
  // a coherent default for both modes, and a Free tenant deserves a
  // polished launcher. The Pro gate is about the *editor* surfacing
  // the customize controls — see ThemeEditor.tsx.
  const darkActive = isDarkMode(effectiveThemeMode, systemPrefersDark);
  const cssVars: Record<string, string> = {};
  if (darkActive) {
    if (profile.theme.paletteDark !== undefined) {
      applyPaletteToCssVars(profile.theme.paletteDark, cssVars);
    }
    // Brand carry: primary + accent are mode-agnostic. Always write
    // them from flat unless paletteDark explicitly set its own value
    // (handled by `??=` against the cssVars already populated above).
    // Lets a Free tenant who edits the flat brand color see it apply
    // in dark mode too, without having to also touch paletteDark.
    cssVars['--ml-primary'] ??= profile.theme.primary;
    cssVars['--ml-accent'] ??= profile.theme.accent;
  } else {
    cssVars['--ml-bg'] = profile.theme.background;
    cssVars['--ml-primary'] = profile.theme.primary;
    cssVars['--ml-accent'] = profile.theme.accent;
    if (profile.theme.surface !== undefined) cssVars['--ml-surface'] = profile.theme.surface;
    if (profile.theme.text !== undefined) cssVars['--ml-text'] = profile.theme.text;
    if (profile.theme.textMuted !== undefined) cssVars['--ml-text-muted'] = profile.theme.textMuted;
    if (profile.theme.paletteLight !== undefined) {
      applyPaletteToCssVars(profile.theme.paletteLight, cssVars);
    }
  }
  // Mirror background → page-bg, surface → tile/card/footer. CSS custom
  // properties resolve var() at the element where they're declared, so
  // a :root rule like `--ml-page-bg: var(--ml-bg, …)` freezes its value
  // at :root and can't be overridden by setting --ml-bg on a descendant.
  // Mirror inline so the user's chosen surface actually reaches paint.
  // Mirrors don't overwrite explicit palette fields — `??=` skips when
  // paletteLight/Dark already set tileBg/cardBg/footerBg explicitly.
  const bgForPage = cssVars['--ml-bg'];
  if (bgForPage !== undefined && cssVars['--ml-page-bg'] === undefined) {
    cssVars['--ml-page-bg'] = bgForPage;
  }
  const surface = cssVars['--ml-surface'];
  if (surface !== undefined) {
    cssVars['--ml-tile-bg'] ??= surface;
    cssVars['--ml-card-bg'] ??= surface;
    cssVars['--ml-footer-bg'] ??= surface;
  }
  if (proTheming && profile.theme.fontRef !== undefined) {
    cssVars['--ml-font'] = `${CUSTOM_FONT_FAMILY}, system-ui, sans-serif`;
  }

  const cycleThemeMode = useCallback(() => {
    setThemeOverride((prev) => {
      const current = prev ?? configDarkMode;
      const next: ThemeMode =
        current === 'auto' ? 'always' : current === 'always' ? 'never' : 'auto';
      // Don't poison localStorage when the cycle button is clicked
      // inside the Builder's preview iframe — see isEmbedded note above.
      if (!isEmbedded) persistThemeOverride(next);
      return next;
    });
  }, [configDarkMode, isEmbedded]);

  // Signage mode — Pro feature for unattended displays. Hides the
  // header + footer chrome (no menu icons, no status strip) and lights
  // up `data-signage="on"` so CSS can scale the tile grid up for
  // distance viewing. The exit gesture still works (admin can long-
  // press the corner to break out) so the device isn't bricked.
  const signageMode = features.has('signageMode');

  // Tile search: offered only when the grid carries enough launchable
  // tiles to be hard to scan. Signage mode hides all chrome, so no
  // search there either.
  const launchableCount = profile.grid.tiles.filter(isLaunchableTile).length;
  const showSearch = !signageMode && launchableCount >= SEARCH_MIN_TILES;
  const normalizedQuery = tileQuery.trim().toLowerCase();
  const searching = showSearch && normalizedQuery !== '';
  const visibleGrid = searching
    ? {
        ...profile.grid,
        tiles: profile.grid.tiles.filter(
          (t) => isLaunchableTile(t) && tileMatchesQuery(t, normalizedQuery),
        ),
      }
    : profile.grid;

  return (
    <div
      className="profile-view"
      style={cssVars as CSSProperties}
      data-theme={dataTheme}
      data-signage={signageMode ? 'on' : undefined}
      data-hero={profile.hero?.kind ?? 'featured'}
      data-testid="profile-view"
    >
      {!signageMode && (
        <LauncherHeader
          title={profile.name}
          {...(deviceLabel !== undefined && { deviceLabel })}
          onOpenDeviceInfo={() => openTop('device-info')}
          onOpenTools={() => openTop('tools')}
          {...(onRefresh !== undefined && { onRefresh })}
          {...(unreadBroadcasts !== undefined && {
            unreadBroadcasts,
            onOpenBroadcasts: () => openTop('broadcasts'),
          })}
          themeMode={effectiveThemeMode}
          onCycleThemeMode={cycleThemeMode}
          statusBar={profile.statusBar}
          features={features}
          {...(currentUser !== undefined && { currentUser })}
          {...(logoSrc !== undefined && { logoSrc })}
          {...(deviceStatus.batteryLevel !== undefined && {
            batteryLevel: deviceStatus.batteryLevel,
          })}
          {...(deviceStatus.batteryCharging !== undefined && {
            batteryCharging: deviceStatus.batteryCharging,
          })}
          {...(deviceStatus.signalDbm !== undefined && { signalDbm: deviceStatus.signalDbm })}
        />
      )}
      <main
        className="profile-view__main"
      >
        {!searching && (
          <LauncherHero
            {...(profile.hero !== undefined && { hero: profile.hero })}
            {...(heroImageSrc !== undefined ? { imageSrc: heroImageSrc } : {})}
            featuredTiles={profile.featuredTiles}
            onTileActivate={handleTileTap}
          />
        )}
        <section className="profile-view__grid-section" aria-label="Apps">
          {showSearch && (
            <div className="launcher-search">
              <svg className="launcher-search__icon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="search"
                className="launcher-search__input"
                placeholder="Search apps…"
                value={tileQuery}
                onChange={(e) => setTileQuery(e.target.value)}
                aria-label="Search apps"
                data-testid="launcher-search"
              />
              {tileQuery !== '' && (
                <button
                  type="button"
                  className="launcher-search__clear"
                  onClick={() => setTileQuery('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          )}
          {searching && visibleGrid.tiles.length === 0 ? (
            <p className="launcher-search__empty" data-testid="launcher-search-empty">
              No apps match “{tileQuery.trim()}”.
            </p>
          ) : (
            <TileGrid grid={visibleGrid} onTileActivate={handleTileTap} />
          )}
        </section>
      </main>
      {!signageMode && (
        <LauncherFooter buildVersion={PLATFORM_VERSION} connected lastSync="just now" />
      )}

      {openModal === 'device-info' && (
        <DeviceInfoModal
          {...(engine && { engine })}
          onClose={closeAllModals}
          {...(onBack && { onBack })}
        />
      )}
      {openModal === 'tools' && (
        <ToolsModal
          onClose={closeAllModals}
          onOpenCalculator={() => pushModal('calculator')}
          onOpenDnsPing={() => pushModal('dns-ping')}
          onOpenDeviceInfo={() => pushModal('device-info')}
          onOpenSpeedTest={() => pushModal('speed-test')}
          profile={profile}
          features={features}
          {...(engine && { engine })}
          {...(onBack && { onBack })}
        />
      )}
      {openModal === 'calculator' && (
        <CalculatorModal
          onClose={closeAllModals}
          {...(onBack && { onBack })}
        />
      )}
      {openModal === 'dns-ping' && (
        <DnsPingModal
          onClose={closeAllModals}
          {...(onBack && { onBack })}
        />
      )}
      {openModal === 'speed-test' && (
        <SpeedTestModal
          onClose={closeAllModals}
          {...(onBack && { onBack })}
        />
      )}
      {openModal === 'broadcasts' && (
        <BroadcastPanel
          broadcasts={visibleBroadcasts}
          onClose={closeAllModals}
          onDismiss={dismissBroadcast}
        />
      )}
      {openFolder !== null && (
        <FolderModal
          folder={openFolder}
          onTileActivate={onTileActivate}
          onClose={() => setOpenFolder(null)}
        />
      )}
    </div>
  );
}

function resolveAsset(ref: string): string {
  return `/assets/${ref}`;
}

/**
 * Minimum launchable-tile count before the in-launcher search bar
 * appears. Below this a kiosk's grid is scannable at a glance, so the
 * search field would just be chrome. ~12 ≈ a dense 4×3 screen.
 */
const SEARCH_MIN_TILES = 12;

/** Spacers / dividers are layout, not launchable targets. */
function isLaunchableTile(tile: Tile): boolean {
  return tile.type !== 'spacer' && tile.type !== 'divider';
}

/**
 * Free-text match for the launcher search: tile label plus the type's
 * target (app package / web URL / agent action), and — for folders —
 * the folder label and any child's label/target, so searching "mail"
 * surfaces a folder that holds the mail app.
 */
function tileMatchesQuery(tile: Tile, q: string): boolean {
  const hay: string[] = [];
  if (typeof tile.label === 'string') hay.push(tile.label);
  if (tile.type === 'app') {
    hay.push(tile.packageName);
  } else if (tile.type === 'web') {
    hay.push(tile.url);
  } else if (tile.type === 'agentAction') {
    hay.push(tile.action);
  } else if (tile.type === 'folder') {
    for (const child of tile.children) {
      if (typeof child.label === 'string') hay.push(child.label);
      if (child.type === 'app') hay.push(child.packageName);
      else if (child.type === 'web') hay.push(child.url);
    }
  }
  return hay.join(' ').toLowerCase().includes(q);
}

/**
 * Map from Palette schema field → CSS custom property. Every field is
 * optional on the schema; the helpers below skip undefined values so
 * customers paint only the surfaces they care about.
 */
const PALETTE_FIELD_TO_CSS_VAR: Readonly<Record<keyof Palette, string>> = {
  background: '--ml-bg',
  surface: '--ml-surface',
  primary: '--ml-primary',
  accent: '--ml-accent',
  text: '--ml-text',
  textMuted: '--ml-text-muted',
  headerBg: '--ml-header-bg',
  headerInk: '--ml-header-ink',
  footerBg: '--ml-footer-bg',
  heroBg: '--ml-hero-bg',
  tileBg: '--ml-tile-bg',
  tileBorder: '--ml-tile-border',
  cardBg: '--ml-card-bg',
  cta: '--ml-cta',
  iconAccent: '--ml-icon-accent',
  divider: '--ml-divider',
};

function applyPaletteToCssVars(p: Palette, target: Record<string, string>): void {
  for (const [field, cssVar] of Object.entries(PALETTE_FIELD_TO_CSS_VAR)) {
    const value = p[field as keyof Palette];
    if (typeof value === 'string' && value.length > 0) {
      target[cssVar] = value;
    }
  }
}

type ThemeMode = 'auto' | 'always' | 'never';

const THEME_OVERRIDE_KEY = 'mobilauncher:theme-override';

function readThemeOverride(): ThemeMode | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(THEME_OVERRIDE_KEY);
    return v === 'auto' || v === 'always' || v === 'never' ? v : null;
  } catch {
    // localStorage can throw in private-browsing or storage-blocked contexts.
    return null;
  }
}

function persistThemeOverride(mode: ThemeMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_OVERRIDE_KEY, mode);
  } catch {
    // No-op: a kiosk WebView with storage disabled still works, the override
    // just won't survive a reload.
  }
}

/**
 * Resolve `effectiveThemeMode` + system preference to a boolean for
 * "is dark mode active right now?". 'always' is the hard pin; 'auto'
 * defers to the OS media-query; 'never' is hard light.
 */
function isDarkMode(mode: ThemeMode, systemPrefersDark: boolean): boolean {
  if (mode === 'always') return true;
  if (mode === 'never') return false;
  return systemPrefersDark;
}
